<template>
  <div class="max-w-2xl p-8">
    <div class="flex justify-between items-center">
      <h2
        class="mb-2"
        v-html="membershipGroup.attributes.title"
      />
      <div class="hidden sm:flex">
        <a
          v-can="'edit-membership-groups'"
          class="btn btn--icon-only icon icon--small mr-6 h-6 w-6"
          href="#"
          @click.prevent="openEditMembershipGroupModal"
        >
          <svg
            viewBox="0 0 9 15"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.67 2.48l.66-1.23L6.25.21 5.56 1.5zm-2.35-.54L.48 10.95l2.11.98 4.84-9.01zM.31 11.43L.1 14.11l2.21-1.75z"
            />
          </svg>
        </a>
        <a
          v-can="'edit-membership-groups'"
          class="btn btn--danger  btn--icon-only icon h-6 w-6"
          href="#"
          @click.prevent="openDeleteMembershipGroupModal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
        </a>
      </div>
    </div>
    <p
      class="mb-2 text-xl"
      v-html="membershipGroup.attributes.description"
    />
    <p class="mb-2">
      <strong v-text="'Group type:'" /> {{ membershipGroup.attributes.type }}
    </p>
    <p
      v-can="'edit-membership-groups'"
      v-text="selfAssignableSentence"
    />
    <div class="flex sm:hidden">
      <a
        v-can="'edit-membership-groups'"
        class="btn btn--sm btn--icon-only icon icon--small mr-6"
        href="#"
        @click.prevent="openEditMembershipGroupModal"
      >
        <svg
          height="15"
          viewBox="0 0 9 15"
          width="9"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7.67 2.48l.66-1.23L6.25.21 5.56 1.5zm-2.35-.54L.48 10.95l2.11.98 4.84-9.01zM.31 11.43L.1 14.11l2.21-1.75z"
          />
        </svg>
      </a>
      <a
        v-can="'edit-membership-groups'"
        class="btn btn--danger btn--sm btn--icon-only icon"
        href="#"
        @click.prevent="openDeleteMembershipGroupModal"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          class="h-12"
        >
          <path
            fill-rule="evenodd"
            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
            clip-rule="evenodd"
          />
        </svg>
      </a>
    </div>
    <h4
      class="mb-6"
      v-text="'Members'"
    />
    <div class="flex flex-wrap sm:flex-no-wrap">
      <div class="mr-8 w-full">
        <input
          v-model="queryString.search"
          placeholder="Search Current Group Members"
          type="text"
        >
      </div>
      <div v-if="canGroupAdminEditMembershipGroup">
        <button
          type="button"
          class="font-white btn btn-blue"
          :disabled="loading"
          @click.prevent="showAddMembersToMembershipGroupModal"
          v-text="'Add members'"
        />
      </div>
    </div>
    <section
      v-if="memberships.data.length"
      class="flex flex-wrap items-stretch mt-2 -ml-2"
    >
      <div
        is="MembershipGroupMember"
        v-for="membership in memberships.data"
        :key="membership.uuid"
        :membership="membership"
        :can-group-admin-edit-membership-group="canGroupAdminEditMembershipGroup"
        :group-admins-enabled="groupAdminsEnabled"
        :removal-queue="removalQueue"
        :group-admin-queue="groupAdminQueue"
        @toggleMemberForRemoval="toggleMemberInQueue(membership,'removalQueue')"
        @toggleGroupAdminAbilities="toggleMemberInQueue(membership,'groupAdminQueue')"
      />
    </section>
    <div
      v-else
      v-text="'No Results'"
    />
    <Pagination
      v-if="! loading"
      :pagination="memberships.meta.pagination"
    />
    <div
      v-if="removalQueue.length && canGroupAdminEditMembershipGroup"
      class="fixed text-center pin-x py-4 pin-b bg-grey-light border-t border-grey-dark z-10"
    >
      <button
        type="button"
        class="btn"
        @click.prevent="removeMembersFromGroup"
        v-text="`Remove selected from group (${removalQueue.length})`"
      />
      <a
        class="mx-4 px-2"
        @click.prevent="removalQueue = []"
        v-text="'Cancel'"
      />
    </div>
    <div
      v-if="groupAdminQueue.length && groupAdminsEnabled"
      v-can="'edit-membership-groups'"
      class="fixed text-center pin-x py-4 pin-b bg-grey-light border-t border-grey-dark z-10"
    >
      <button
        type="button"
        class="btn"
        @click.prevent="toggleGroupAdmin"
        v-text="`Update Selected Group Admin Abilities (${groupAdminQueue.length})`"
      />
      <a
        class="mx-4 px-2"
        @click.prevent="groupAdminQueue = []"
        v-text="'Cancel'"
      />
    </div>
    <AddMembersToMembershipGroupModal @membershipsHaveBeenAdded="getGroupMemberships" />
    <EditMembershipGroupModal
      :groups-listed-inside-calendar="groupsListedInsideCalendar"
      @membershipGroupHasBeenUpdated="getGroupMemberships"
    />
    <DeleteMembershipGroupModal @membershipGroupHasBeenDeleted="groupDeletedListener" />
  </div>
</template>

<script type="text/babel">
import AddMembersToMembershipGroupModal from './_modals/AddMembersToMembershipGroupModal'
import EditMembershipGroupModal from './_modals/EditMembershipGroupDetailsModal'
import DeleteMembershipGroupModal from './_modals/DeleteMembershipGroupModal'
import MembershipGroupMember from './MembershipGroupMember'
import Pagination from 'Base/Resources/assets/js/components/Pagination'
import { debounce } from 'lodash'
import URI from 'urijs'

export default {
  name: 'MembershipGroupDetails',
  components: {
    EditMembershipGroupModal,
    AddMembersToMembershipGroupModal,
    MembershipGroupMember,
    DeleteMembershipGroupModal,
    Pagination,
  },
  props: {
    membershipGroupUuid: {
      type: String,
      required: true,
    },
    groupsListedInsideCalendar: {
      type: Array,
      required: false,
      default: () => [],
    },
    groupAdminsEnabled: {
      type: Boolean,
      required: true,
    },
    maxGroupAdmins: {
      type: Number,
      required: true,
    },
    canGroupAdminEditMembershipGroup: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      queryString: {
        search: '',
        page: 1,
      },
      membershipGroup: {
        attributes: {
          title: '',
          type: '',
          description: '',
          self_assignable: false,
        },
      },
      memberships : {
        data: [],
        _links: {},
        meta: {
          pagination: {},
        },
      },
      removalQueue: [],
      groupAdminQueue: [],
    }
  },
  computed: {
    selfAssignableSentence() {
      return this.membershipGroup.attributes.self_assignable
        ? 'Members are allowed to join this group at registration.'
        : 'Members are not allowed to join this group at registration.'
    },
    fetchUrl() {
      return new URI('').setSearch(this.queryString).toString()
    },
  },
  watch: {
    queryString: {
      handler: debounce(function () {
        history.pushState(null, null, this.fetchUrl)

        this.getGroupMemberships()
      }, 500),
      deep: true,
    },
  },
  async mounted() {
    await this.$nextTick()

    this.queryString = Object.assign(this.queryString, new URI().query(true))

    this.getGroupMemberships()
  },
  methods: {
    openEditMembershipGroupModal() {
      this.$store.commit('membershipOpenEditMembershipGroupModal', this.membershipGroup)
    },
    openDeleteMembershipGroupModal() {
      this.$store.commit('membershipOpenDeleteMembershipGroupModal', this.membershipGroup)
    },
    showAddMembersToMembershipGroupModal() {
      this.$store.commit('membershipOpenAddMembersToMembershipGroupModal', this.membershipGroup)
    },
    getGroupMemberships() {
      this.loading = true

      let uri = `memberships/membership-groups/${this.membershipGroupUuid}?`

      if (this.queryString.page) { uri += `page=${this.queryString.page}` }
      if (this.queryString.search) { uri += `&search=${this.queryString.search}` }

      this.$api.active().get(uri)
        .then((response) => {
          this.memberships = response.data

          this.membershipGroup = response.data.meta.membershipGroup

          this.loading = false
        })
    },
    removeMembersFromGroup() {
      this.$api.active().post(`memberships/membership-groups/${this.membershipGroupUuid}/remove-members`, {
        membershipUuids: this.removalQueue,
      }).then((response) => {
        this.getGroupMemberships()

        this.removalQueue = []

        this.$store.commit('baseAddNotification', {
          title: 'Success!',
          text: response.data.message,
          type: 'success',
        })
      })
    },
    toggleGroupAdmin() {
      this.$api.active().post(`memberships/membership-groups/${this.membershipGroupUuid}/toggle-group-admin`, {
        membershipUuids: this.groupAdminQueue,
      }).then((response) => {
        this.getGroupMemberships()

        this.groupAdminQueue = []

        this.$store.commit('baseAddNotification', {
          title: 'Success!',
          text: response.data.message,
          type: 'success',
        })
      }).catch((errors) => {
        this.$store.commit('baseAddNotification', {
          type: 'error',
          title: 'Error',
          text: errors.response.data.errors.message[0],
        })
      })
    },
    toggleMemberInQueue(member, queue) {
      if (this[queue].includes(member.uuid)) {
        return this[queue].splice(this[queue].indexOf(member.uuid), 1)
      }

      return this[queue].push(member.uuid)
    },
    groupDeletedListener() {
      window.location = this.$urlGenerator.active('members/membership-groups')
    },
  },
}
</script>
